/* eslint-disable eqeqeq */
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    APPLICATION_CONTAINER_ID, STATUS_OK, STRATEGIES, strategyTypes, THREEBTYPES,
} from 'constants/constants';

import { useModelPortfolioList } from 'hooks/rest/useModelPortfolioList';
import ServiceManager from 'services/ServiceManager';
import { notification } from 'ui-library';

import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { NO_EXCLUSION_PREFERENCE } from 'pages/ClientOverview/pages/Portfolios/constants';
import { useQueryClient } from '@tanstack/react-query';
import { ModalStatusProps } from './ShoppingCartProductRow.types';
import { useWithdrawalPlanChangePlan } from './useWithdrawalPlanChangePlan';
import useCallbackState from '../../../hooks/useCallbackState';

export interface CaseInvestmentPlan {
    isSell: boolean;
    amount: number;
    isCaseSource: boolean;
}

const useExistingProducts = ({
    portfolio,
    language,
    t,
    advisoryDocumentId,
    saveInvestmentApplicationDataToStoreAction,
    removeInvestmentApplicationDataAction,
    optionPreferenceStock,
    optionPreferenceExclusion,
    optionPreferenceInclusion,
}): any => {
    const {
        products: modelPortfolios,
        strategies,
        isLoading: modelPortfoliosLoading,
        refetch: refetchModelPortfolios,
    } = useModelPortfolioList({
        productId: portfolio?.existingProduct?.id,
        enabled: false,
    });

    const queryClient = useQueryClient();

    const changedInvestmentStrategy = useMemo(() => portfolio?.changedStrategy?.investmentDescription?.selectStrategy?.modelPortfolioId, [portfolio?.changedStrategy?.investmentDescription?.selectStrategy?.modelPortfolioId]);

    const selectedModelPortfolio = useMemo(
        () => modelPortfolios?.find(item => item?.Id === (changedInvestmentStrategy || portfolio?.modelPortfolioId)),
        [modelPortfolios, portfolio?.modelPortfolioId, changedInvestmentStrategy],
    );

    const selectedStrategyId = useMemo(() => selectedModelPortfolio?.RiskCategory?.Id, [selectedModelPortfolio?.RiskCategory?.Id]);

    const [strategy, setStrategy] = useState(changedInvestmentStrategy || portfolio?.modelPortfolioId);

    const [investment, setInvestment] = useCallbackState<CaseInvestmentPlan>({
        isCaseSource: portfolio?.isCaseSource ?? false,
        isSell: portfolio?.isSell ?? false,
        amount: portfolio?.changedAmount || 0,
    });

    const [selectedStrategyIdState, setSelectedStrategyIdState] = useState<number | null>(selectedStrategyId);
    const [preferenceStockState, setPreferenceStockState] = useState<string | undefined>(undefined);
    const [preferenceExclusionState, setPreferenceExclusionState] = useState<string | undefined>(undefined);
    const [exclusionSubstituteState, setExclusionSubstituteState] = useState<string | undefined>(undefined);
    const [preferenceInclusionState, setPreferenceInclusionState] = useState<string | undefined>(undefined);

    const [selectedProductMarkId, setSelectedProductMarkId] = useState(0);
    const [isWithdrawalSelected, setIsWithdrawalSelected] = useState(false);
    const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);

    useEffect(() => {
        if (isWithdrawalSelected && selectedStrategyId > 2) {
            setSelectedStrategyIdState(STRATEGIES.income);
        } else {
            setSelectedStrategyIdState(selectedStrategyId);
        }
        setPreferenceStockState(undefined);
        setPreferenceExclusionState(undefined);
        setExclusionSubstituteState(undefined);
        setPreferenceInclusionState(undefined);
        setSelectedProductMarkId(selectedModelPortfolio?.Id);
    }, [isWithdrawalSelected, selectedModelPortfolio?.Id]);

    const isCapital = selectedStrategyIdState === STRATEGIES.capital;

    const [optionExclusionSubstitute, setOptionExclusionSubstitute] = useState<any[]>([]);

    const NO_EXCLUSION = optionPreferenceExclusion ? optionPreferenceExclusion[0]?.value : 0;

    useEffect(() => {
        if (isCapital) {
            if (
                (!preferenceStockState || !preferenceInclusionState)
                && optionPreferenceExclusion?.length
                && optionPreferenceStock?.length
                && optionPreferenceInclusion
                && optionPreferenceExclusion?.length > 0
                && optionPreferenceStock?.length > 0
            ) {
                if (!preferenceStockState) {
                    setPreferenceStockState(
                        optionPreferenceStock[0]?.value as string,
                    );
                }
                setPreferenceInclusionState(
                    optionPreferenceInclusion[0]?.value as string,
                );
            }
        } else if (
            (!preferenceStockState || !preferenceExclusionState)
            && optionPreferenceStock
            && optionPreferenceExclusion
            && optionPreferenceExclusion?.length > 0
            && optionPreferenceStock?.length > 0
        ) {
            setPreferenceStockState(optionPreferenceStock[0]?.value as string);
            setPreferenceExclusionState(
                optionPreferenceExclusion[0]?.value as string,
            );
            setExclusionSubstituteState(undefined);
        }
    }, [
        selectedStrategyId,
        optionPreferenceStock,
        optionPreferenceExclusion,
        optionPreferenceInclusion,
        isCapital,
        preferenceStockState,
        preferenceExclusionState,
        preferenceInclusionState,
    ]);

    useEffect(() => {
        if (selectedStrategyId) {
            setSelectedStrategyIdState(selectedStrategyId);
        }

        setStrategy(changedInvestmentStrategy || portfolio?.modelPortfolioId);
    }, [selectedStrategyId, changedInvestmentStrategy, portfolio?.modelPortfolioId]);

    const getSubstituteExclusionOptions = async () => {
        try {
            const { data } = await ServiceManager.customInvestmentService(
                'getSubstituteExclusionOptions',
                [
                    {
                        filter: optionPreferenceExclusion?.find(
                            (i) => i?.value === preferenceExclusionState,
                        )?.label,
                        preferenceKey: 'substituteExclusion',
                        language,
                    },
                ],
            );

            if (data) {
                const tempSubstituteExclusionOptions = data?.items?.map(
                    (item) => ({
                        ...item,
                        value: `${item.id}`,
                    }),
                );

                setOptionExclusionSubstitute(tempSubstituteExclusionOptions);
            }
            setExclusionSubstituteState(undefined);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (preferenceExclusionState && preferenceExclusionState !== NO_EXCLUSION) {
            getSubstituteExclusionOptions();
        }
    }, [preferenceExclusionState, language]);

    const {
        mutateAsync: handleChangeExisting3bPlan,
        isPending: changeExisting3bPlanResponseIsPending,
    } = useGenericAPIMutation();

    const [modalStatus, setModalStatus] = useState<ModalStatusProps>({ open: false, product: undefined, field: undefined });

    const isZIC = useMemo(() => portfolio?.existingProduct?.externalId === THREEBTYPES.zic, [portfolio?.existingProduct?.externalId]);
    const isPK = useMemo(() => portfolio?.existingProduct?.externalId === THREEBTYPES.pk, [portfolio?.existingProduct?.externalId]);
    const showEditIcon = useMemo(() => portfolio?.editable && !portfolio?.pendingCEPRequest, [portfolio?.editable, portfolio?.pendingCEPRequest]);
    const isAzpOrAzpOld = useMemo(() => (portfolio?.existingProduct?.externalId && [THREEBTYPES.azpAlt, THREEBTYPES.azpNeu, THREEBTYPES.azpNeuSB]?.includes(portfolio?.existingProduct?.externalId)) || false, [portfolio?.existingProduct?.externalId]);

    useEffect(() => {
        if (selectedStrategyId === STRATEGIES.individual) {
            setStrategy(undefined);
        } else {
            setStrategy(changedInvestmentStrategy || portfolio?.modelPortfolioId);
        }

        setInvestment({
            isCaseSource: portfolio?.isCaseSource ?? false,
            isSell: (isZIC || isPK) ? true : (portfolio?.isSell ?? false),
            amount: portfolio?.changedAmount || 0,
        });
    }, [portfolio?.modelPortfolioId, portfolio?.changedAmount, portfolio?.isSell, portfolio?.isCaseSource, selectedStrategyId, isZIC, isPK]);

    const selectedStrategy = useMemo(() => strategies?.find(item => item?.Id === selectedStrategyIdState), [strategies, selectedStrategyIdState]);

    const getIsStrategyDisabled = useCallback(
        (strtgy) => {
            if (!selectedStrategy?.Products?.includes(strtgy?.Id) || strtgy?.Id === (changedInvestmentStrategy || portfolio?.modelPortfolioId) || strtgy?.WithdrawalOption !== isWithdrawalSelected) {
                return true;
            }

            return false;
        },
        [portfolio?.modelPortfolioId, changedInvestmentStrategy, isWithdrawalSelected, selectedStrategy?.Products],
    );

    const strategyOptions = useMemo(() => modelPortfolios?.filter(p => p?.Type !== strategyTypes.INDIVIDUAL)?.map((port) => ({
        value: port?.Id,
        label: port?.Name,
        disabled: getIsStrategyDisabled(port),
    })), [modelPortfolios, getIsStrategyDisabled]);

    const baseStrategyOptions = useMemo(() => strategies?.map((strtg) => ({
        value: strtg?.Id,
        label: strtg?.Name,
        disabled: (modalStatus?.product !== THREEBTYPES.zivv && strtg?.Id === selectedStrategyId)
            || strtg?.Id === STRATEGIES.individual
            || (isWithdrawalSelected && strtg?.Id > 2),
    })), [strategies, selectedStrategyId, isWithdrawalSelected]);

    const selectedProduct = useMemo(() => modelPortfolios?.find(item => item.Id === selectedProductMarkId) || {}, [modelPortfolios, selectedProductMarkId]);

    const continueDisabled = useMemo(() => !selectedProduct?.Id, [selectedProduct?.Id]);
    const selectedWithdrawalOption = useMemo(() => (portfolio?.changedStrategy ? !!portfolio?.changedStrategy?.investmentDescription?.withdrawal : !!portfolio?.withdrawalAmount), [portfolio?.changedStrategy, portfolio?.changedStrategy?.investmentDescription?.withdrawal, portfolio?.withdrawalAmount]);

    const handleEditStrategy = useCallback((data: ModalStatusProps) => {
        setModalStatus(data);
        setIsWithdrawalSelected(selectedWithdrawalOption);
        refetchModelPortfolios();
    }, [selectedWithdrawalOption]);

    const onChangeStrategy = useCallback(async ({ withdrawalPlanData }) => {
        const containerId = sessionStorage.getItem(APPLICATION_CONTAINER_ID);
        const uuid = uuidv4();

        if (!containerId) {
            sessionStorage.setItem(APPLICATION_CONTAINER_ID, uuid);
        }

        const payloadModelPortfolioId = portfolio?.existingProduct?.externalId === THREEBTYPES?.zivv ? selectedProduct?.Id : strategy;

        if (!!portfolio?.id && payloadModelPortfolioId) {
            let result: any = {};

            if (portfolio?.changedStrategy?.investmentApplicationId) {
                result = await handleChangeExisting3bPlan({
                    service: 'customInvestmentService',
                    API: 'putChangeExisting3bStrategy',
                    payload: {
                        applicationId: portfolio?.changedStrategy?.investmentApplicationId,
                        modelPortfolioId: payloadModelPortfolioId as number,
                        strategyId: selectedStrategyIdState as number,
                        withdrawalOption: withdrawalPlanData?.withdrawalOption || false,
                        withdrawal: withdrawalPlanData?.withdrawalOption ? withdrawalPlanData?.withdrawal : null,
                        paymentInstruction: withdrawalPlanData?.withdrawalOption ? withdrawalPlanData?.paymentInstruction : null,
                    },
                });
            } else {
                result = await handleChangeExisting3bPlan({
                    service: 'customInvestmentService',
                    API: 'postChangeExisting3bStrategy',
                    payload: {
                        portfolioId: portfolio?.id as number,
                        modelPortfolioId: payloadModelPortfolioId as number,
                        containerId: (containerId || uuid) as string,
                        advisoryDocumentId,
                        strategyId: selectedStrategyIdState as number,
                        withdrawalOption: withdrawalPlanData?.withdrawalOption || false,
                        withdrawal: withdrawalPlanData?.withdrawalOption ? withdrawalPlanData?.withdrawal : null,
                        paymentInstruction: withdrawalPlanData?.withdrawalOption ? withdrawalPlanData?.paymentInstruction : null,
                    },
                });
            }

            if (result?.status === STATUS_OK) {
                saveInvestmentApplicationDataToStoreAction({ payload: { ...result?.data, changePlan: true } });
            }

            await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });

            setModalStatus({
                open: false,
                product: undefined,
                field: undefined,
            });
        }
    }, [
        strategy,
        selectedProduct,
        portfolio?.id,
        portfolio?.changedStrategy?.investmentApplicationId,
        queryClient,
        advisoryDocumentId,
        selectedStrategyIdState,
        saveInvestmentApplicationDataToStoreAction,
        handleChangeExisting3bPlan,
    ]);

    const onChangeInvestmentPlan = useCallback(async (investmentPlan: CaseInvestmentPlan) => {
        const containerId = sessionStorage.getItem(APPLICATION_CONTAINER_ID);
        const uuid = uuidv4();

        if (!containerId) {
            sessionStorage.setItem(APPLICATION_CONTAINER_ID, uuid);
        }

        if (portfolio?.id) {
            let result: any = {};

            const defaultPayload = {
                isSell: investmentPlan.isSell,
                amount: investmentPlan.amount,
                isCaseSource: isAzpOrAzpOld ? investmentPlan.isCaseSource : null,
            };

            if (portfolio?.changedBuySellAmount?.investmentApplicationId) {
                result = await handleChangeExisting3bPlan({
                    service: 'customInvestmentService',
                    API: 'putChangeExisting3bInvestmentAmount',
                    payload: {
                        applicationId: portfolio?.changedBuySellAmount?.investmentApplicationId,
                        ...defaultPayload,
                    },
                });
            } else {
                result = await handleChangeExisting3bPlan({
                    service: 'customInvestmentService',
                    API: 'postChangeExisting3bInvestmentAmount',
                    payload: {
                        portfolioId: portfolio?.id as number,
                        advisoryDocumentId,
                        containerId: (containerId || uuid) as string,
                        ...defaultPayload,
                    },
                });
            }
            if (result?.status === STATUS_OK) {
                saveInvestmentApplicationDataToStoreAction({ payload: { ...result?.data, changePlan: true } });
            }
            await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });
            setModalStatus({
                open: false,
                product: undefined,
                field: undefined,
            });
        }
    }, [
        portfolio?.id, 
        portfolio?.changedBuySellAmount?.investmentApplicationId, 
        advisoryDocumentId, 
        queryClient, 
        isAzpOrAzpOld, 
        handleChangeExisting3bPlan, 
        saveInvestmentApplicationDataToStoreAction
    ]);

    const onInvestmentPlanUpdate = useCallback((investmentPlan: CaseInvestmentPlan) => {
        setInvestment({ ...investmentPlan }, (updatedPlan) => { onChangeInvestmentPlan(updatedPlan); });
    }, [setInvestment, onChangeInvestmentPlan]);

    useEffect(() => {
        if (!selectedStrategy || modalStatus?.product !== THREEBTYPES.zivv) {
            return;
        }

        let tempProducts = modelPortfolios;

        tempProducts = isWithdrawalSelected ? tempProducts.filter(product => JSON.parse(product?.Attributes || '{}')?.WithdrawalOption) : tempProducts.filter(product => !JSON.parse(product?.Attributes || '{}')?.WithdrawalOption);

        const preferenceFilters = {
            preferenceStocks: preferenceStockState && parseInt(preferenceStockState, 10),
            ...(selectedStrategyIdState === STRATEGIES.capital ? { preferenceInclusion: preferenceInclusionState && parseInt(preferenceInclusionState, 10) }
                : {
                    preferenceExclusion: preferenceExclusionState && parseInt(preferenceExclusionState, 10),
                    substituteExclusion: exclusionSubstituteState && parseInt(exclusionSubstituteState, 10),
                }),
        };

        Object.keys(preferenceFilters)
            .forEach(key => ((preferenceFilters[key] === undefined && key !== 'substituteExclusion') || (preferenceFilters[key] === undefined && parseInt(preferenceExclusionState as string, 10) === NO_EXCLUSION_PREFERENCE)) && delete preferenceFilters[key]);

        tempProducts = tempProducts?.filter(product => {
            const productAttributes = JSON.parse(product?.Attributes || '{}');

            const keys = Object.keys(preferenceFilters);
            const isMatching = keys?.every(key => productAttributes[key] === preferenceFilters[key]);

            if (!isMatching) {
                return false;
            }

            return true;
        });

        const strategyProducts = tempProducts?.filter((product) => selectedStrategy?.Products?.includes(product?.Id));

        const product = strategyProducts?.reduce((selected, item) => {
            if (selected.RiskScore > item.RiskScore) {
                return item;
            }

            return selected;
        }, {
            RiskScore: Infinity,
            Id: '',
        });

        if ((selectedStrategyIdState && selectedStrategyIdState !== selectedStrategyId)
            || (exclusionSubstituteState && exclusionSubstituteState !== optionExclusionSubstitute?.[0]?.value)
            || (preferenceExclusionState && preferenceExclusionState !== optionPreferenceExclusion?.[0]?.value)
            || (preferenceStockState && preferenceStockState !== optionPreferenceStock?.[0]?.value)
            || (preferenceInclusionState && preferenceInclusionState !== optionPreferenceInclusion?.[0]?.value)
            || isWithdrawalSelected !== selectedWithdrawalOption
        ) {
            setSelectedProductMarkId(product?.Id);
        }
    }, [
        selectedStrategyIdState,
        modelPortfolios,
        strategies,
        selectedStrategy,
        exclusionSubstituteState,
        preferenceExclusionState,
        preferenceStockState,
        preferenceInclusionState,
        isWithdrawalSelected,
        selectedWithdrawalOption,
    ]);

    const handleDiscard3bApplication = useCallback(() => {
        const func1 = portfolio?.changedStrategy?.investmentApplicationId
            ? handleChangeExisting3bPlan({
                service: 'customInvestmentService',
                API: 'deleteInvestmentApplication',
                payload: { investmentApplicationId: portfolio?.changedStrategy?.investmentApplicationId },
            })
            : null;

        const func2 = portfolio?.changedBuySellAmount?.investmentApplicationId
            ? handleChangeExisting3bPlan({
                service: 'customInvestmentService',
                API: 'deleteInvestmentApplication',
                payload: { investmentApplicationId: portfolio?.changedBuySellAmount?.investmentApplicationId },
            })
            : null;

        const promises = [func1, func2].filter(Boolean);

        Promise.allSettled(promises).then((data) => {
            if (data?.[0]?.value?.status === STATUS_OK) {
                removeInvestmentApplicationDataAction({
                    id: portfolio?.changedStrategy?.investmentApplicationId || portfolio?.changedBuySellAmount?.investmentApplicationId,
                });
            }
            if (data?.[1]?.value?.status === STATUS_OK) {
                removeInvestmentApplicationDataAction({
                    id: portfolio?.changedBuySellAmount?.investmentApplicationId,
                });
            }
            notification.open({
                content: t('dashboard.overviewClientApplications.canceledSuccessfully'),
                type: 'success',
            });
        }).catch(() => {
            notification.open({
                content: t('dashboard.overviewClientApplications.cancelError'),
                type: 'error',
            });
        }).finally(async () => {
            await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });
        });
    }, [t, portfolio?.changedStrategy?.investmentApplicationId, portfolio?.changedBuySellAmount?.investmentApplicationId, removeInvestmentApplicationDataAction, queryClient, handleChangeExisting3bPlan]);

    const withdrawalChangePlanData = useWithdrawalPlanChangePlan({
        portfolio,
        withdrawalOption: portfolio?.withdrawalOption,
        paymentInstruction: portfolio?.paymentInstruction,
        onChangeStrategy,
    });

    return {
        modalStatus,
        setModalStatus,
        showEditIcon,
        isAzpOrAzpOld,
        handleEditStrategy,
        strategy,
        setStrategy,
        onChangeStrategy,
        modelPortfoliosLoading,
        strategyOptions,
        onChangeInvestmentPlan,
        investment,
        onInvestmentPlanUpdate,
        NO_EXCLUSION,
        baseStrategyOptions,
        selectedStrategyIdState,
        setSelectedStrategyIdState,
        optionPreferenceStock,
        preferenceStockState,
        setPreferenceStockState,
        isCapital,
        preferenceInclusionState,
        optionPreferenceInclusion,
        setPreferenceInclusionState,
        optionPreferenceExclusion,
        preferenceExclusionState,
        setPreferenceExclusionState,
        optionExclusionSubstitute,
        exclusionSubstituteState,
        setExclusionSubstituteState,
        isZIC,
        isPK,
        selectedProduct,
        selectedModelPortfolio,
        handleDiscard3bApplication,
        changeExisting3bPlanResponseIsPending,
        isWithdrawalSelected,
        setIsWithdrawalSelected,
        continueDisabled,
        withdrawalModalOpen,
        setWithdrawalModalOpen,
        withdrawalChangePlanData,
    };
};

export default useExistingProducts;
